import {Component, inject, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {BicBadgeComponent} from '@buildinginformationcloud/bic-ui-components';
import {JsonSchema, SelectOption} from '../schema-form.types';
import {SchemaSourceService} from '../schema-source.service';
import {enumLabel} from '../widget-registry';

/**
 * x-widget:badges → reine Anzeige als Badge-Liste (kein Eingabefeld, kein valueChange).
 *
 * Für Werte, die woanders verwaltet werden (z.B. im Regelset) und im Knoten-Editor nur
 * sichtbar sein sollen. Labels werden generisch aufgelöst: `x-source` über den
 * SchemaSourceService (Dokumentenklassen-IDs → Titel), sonst `x-enumLabels`. Skalare und
 * Arrays werden gleich behandelt; ist nichts gesetzt, erscheint `x-empty-label`.
 */
@Component({
  selector: 'sf-badges',
  standalone: true,
  imports: [BicBadgeComponent],
  host: {'class': 'block w-full'},
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-wrap gap-1">
      @if (values.length === 0) {
        <bic-badge bgClass="bg-gray-100" textClass="text-gray-500">{{ emptyLabel }}</bic-badge>
      } @else {
        @for (value of values; track value) {
          <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">{{ labelFor(value) }}</bic-badge>
        }
      }
    </div>
  `,
})
export class SfBadgesComponent implements OnInit {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;

  private options: SelectOption[] = [];
  private readonly sources = inject(SchemaSourceService);

  /**
   * Skalar und Array einheitlich als Liste; leere Einträge fallen heraus.
   *
   * Ist nichts gesetzt, greift der Schema-`default` — das Modul rechnet zur Laufzeit ebenfalls damit,
   * die Anzeige entspricht also dem tatsächlichen Verhalten und nicht „kein Wert“.
   */
  get values(): any[] {
    const raw = Array.isArray(this.value) ? this.value : [this.value];
    const values = raw.filter(v => v !== null && v !== undefined && v !== '');
    if (values.length === 0 && this.schema?.default != null && !Array.isArray(this.schema.default)) {
      return [this.schema.default];
    }
    return values;
  }

  get emptyLabel(): string {
    return this.schema?.['x-empty-label'] ?? '—';
  }

  ngOnInit(): void {
    const source = this.schema?.['x-source'];
    if (source) {
      this.sources.getOptions(source).subscribe(opts => (this.options = opts));
    }
  }

  /**
   * Anzeigetext eines Wertes. Solange die Optionen einer `x-source` noch laden — oder wenn eine
   * gespeicherte ID dort nicht mehr existiert — bleibt der Rohwert stehen, statt die Angabe
   * verschwinden zu lassen.
   */
  labelFor(value: any): string {
    const option = this.options.find(o => o.value === value);
    if (option) return option.label;
    return enumLabel(this.schema, value);
  }
}
